import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { List, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Logo } from "./Logo";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { to: "/listings?listing_type=sale", label: "Buy" },
    { to: "/listings?listing_type=rent", label: "Rent" },
    { to: "/listings", label: "All Residences" },
    { to: "/localities", label: "Localities" },
    { to: "/register?role=agent", label: "Partner With Us" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 border-b ${
        scrolled ? "bg-black/70 backdrop-blur-2xl border-white/10" : "bg-black/0 border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 py-4 lg:px-12">
        <Link to="/" data-testid="nav-home-link" aria-label="Gurgaon Estates home">
          <Logo />
        </Link>
        <nav className="hidden items-center gap-10 lg:flex" data-testid="nav-links">
          {links.map((l) => (
            <Link key={l.label} to={l.to} data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="gold-link">
              {l.label}
            </Link>
          ))}
        </nav>
        <div className="hidden items-center gap-4 lg:flex">
          {user ? (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-button"
                className="border border-gold/60 px-5 py-2 font-mono text-xs uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink"
              >
                Dashboard
              </Link>
              <button onClick={async () => { await logout(); navigate("/"); }} data-testid="nav-logout-button" className="gold-link">
                Sign out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-signin-button"
              className="border border-gold/60 px-5 py-2 font-mono text-xs uppercase tracking-[0.25em] text-gold transition-colors hover:bg-gold hover:text-ink"
            >
              Sign In
            </Link>
          )}
        </div>
        <button className="text-bone lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="border-t border-white/10 bg-black/95 backdrop-blur-2xl px-6 py-6 lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-5">
              {links.map((l) => (
                <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className="gold-link text-sm">
                  {l.label}
                </Link>
              ))}
              {user ? (
                <Link to="/dashboard" onClick={() => setOpen(false)} className="gold-link text-sm">
                  Dashboard
                </Link>
              ) : (
                <Link to="/login" onClick={() => setOpen(false)} className="gold-link text-sm">
                  Sign In
                </Link>
              )}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
